import { BaseOrderAdapter } from "./BaseOrderAdapter.js";
import {
  UNIFIED_ORDER_STATUS,
  UNIFIED_PAYMENT_STATUS,
  UNIFIED_DELIVERY_STATUS,
  UNIFIED_ORDER_TYPE,
  UNIFIED_ORDER_STATUS_LEVEL,
  UNIFIED_ORDER_STATUS_LEVEL_MAP,
} from "../types/UnifiedOrderTypes.js";

export class SwiggyOrderAdapter extends BaseOrderAdapter {
  getPartnerId() {
    return "swiggy";
  }

  getPartnerName() {
    return "Swiggy";
  }

  transformOrder(orderData) {
    let cachedOrderLevel = localStorage.getItem(`partnerOrders`);
    if (cachedOrderLevel) {
      cachedOrderLevel = JSON.parse(cachedOrderLevel);
    } else {
      cachedOrderLevel = {};
    }

    const billAmount =
      Number(orderData.bill) -
      Number(orderData.discount) -
      orderData.GST_details.swiggy_liable_gst;

    let orderPreparationNote =
      orderData.customer?.special_instructions?.si_cx_instruction || "";

    if (orderData.cutleryOptedIn) {
      orderPreparationNote += ` and send cutlery`;
    }

    // Calculate bill transaction logs
    const billTxnLogs = this.calculateBillTxnLogs({
      ...orderData,
      bill: billAmount,
    });

    // Transform items
    const items = this.transformItems(orderData);

    // Transform customer details
    const customer = this.transformCustomer({
      ...orderData,
      instructions: orderPreparationNote,
    });

    // Transform delivery details
    const delivery = this.transformDelivery(orderData);

    // Transform restaurant details
    const restaurant = this.transformRestaurant(orderData);

    // Transform order metadata
    const meta = this.transformOrderMeta(orderData);

    // Get prep time
    const prepTime = this.getPrepTime(orderData);

    let orderStatus = this.getUnifiedStatus(orderData.status);
    let orderStatusLevel = UNIFIED_ORDER_STATUS_LEVEL[orderStatus];

    if (cachedOrderLevel[orderData.order_id]) {
      if (cachedOrderLevel[orderData.order_id] > orderStatusLevel) {
        orderStatus =
          UNIFIED_ORDER_STATUS_LEVEL_MAP[cachedOrderLevel[orderData.order_id]];
        orderStatusLevel = cachedOrderLevel[orderData.order_id];
      } else {
        cachedOrderLevel[orderData.order_id] = orderStatusLevel;
        localStorage.setItem(`partnerOrders`, JSON.stringify(cachedOrderLevel));
      }
    }

    return {
      id: orderData.order_id,
      orderId: orderData.order_id,
      partnerId: this.getPartnerId(),
      partnerOrderId: orderData.order_id,
      partnerName: this.getPartnerName(),
      billNo: orderData.order_id,
      staffId: "",
      tableVPA: "NA",
      orderType: UNIFIED_ORDER_TYPE.PARTNER,
      typeOfOrder: UNIFIED_ORDER_TYPE.DELIVERY,
      orderStatus: orderStatus,
      orderStatusLevel: orderStatusLevel,
      billStatus: orderData.status?.placed_status || "pending",
      amount: Number(billAmount.toFixed(2)) || 0,
      paymentmethod: "PAID", // Swiggy orders are typically prepaid
      partialAmoutsplit: [],
      billTxnLogs,
      discount: orderData.discount || 0,
      tips: 0,
      orderPreparationNote: orderPreparationNote,
      items,
      customer,
      delivery,
      restaurant,
      meta,
      createdAt: orderData.status?.ordered_time,
      updatedAt: orderData?.last_updated_time,
      acceptedAt: orderData.status?.placed_time,
      canceled: orderData.status?.cancelled_time !== null,
      cancelReason: "",
      orderOTP: "", // Swiggy doesn't provide OTP in the response
      rawOrder: orderData,
      prepTime,
    };
  }

  calculateBillTxnLogs(orderData) {
    const gstDetails = orderData.GST_details || {};
    return {
      beforeDiscountTotal:
        orderData.cart?.items?.reduce(
          (total, item) => total + (item.sub_total || 0),
          0
        ) || 0,
      cgst: Number(gstDetails.restaurant_liable_gst / 2) || 0,
      cgstPercent: "2.5",
      discount: orderData.discount || 0,
      discountFlat: orderData.discount || 0,
      discountPercent: 0,
      gst: true,
      gstExcluded: false,
      gstNo: "",
      service: false,
      sgst: Number(gstDetails.restaurant_liable_gst / 2) || 0,
      sgstPercent: "2.5",
      subTotal:
        orderData.cart?.items?.reduce(
          (total, item) => total + (item.total || 0),
          0
        ) || 0,
      tip: 0,
      total: Number(orderData.bill.toFixed(2)) || 0,
      paymentType: "online",
      showGSTFooter: false,
    };
  }

  transformItems(orderData) {
    return (
      orderData.cart?.items?.map((item) => ({
        id: item.item_id,
        itemMenuId: item.item_id,
        feUniqueItemId: `${item.item_id}`,
        name: item.name,
        quantity: item.quantity,
        price: Number(item.total / item.quantity).toFixed(2),
        variantName: "",
        variantId: "",
        isVariant: false,
        isExtra: false,
        extra: false,
        itemPreparationNote: "",
        itemCancelReason: "",
        status: "preparing",
        isCancelled: false,
        category: item.category,
        isVeg: item.is_veg === "1",
        addons:
          item.addons?.map((addon) => ({
            id: addon.id,
            name: addon.name,
            price: addon.price || 0,
          })) || [],
        variants: [],
        createdAt: orderData.status?.ordered_time,
        updatedAt: orderData.last_updated_time,
      })) || []
    );
  }

  transformCustomer(orderData) {
    return {
      name: orderData.customer?.customer_name || "",
      phone: orderData.customer?.customer_id || "",
      address: {
        text: orderData.customer_area || "",
        location: {
          lat: orderData.customer?.customer_lat || 0,
          lng: orderData.customer?.customer_lng || 0,
        },
      },
      instructions: orderData.instructions,
    };
  }

  transformDelivery(orderData) {
    return {
      person: {
        image: orderData.delivery_boy?.imageUrl || null,
        name: orderData.delivery_boy?.name || null,
        phone: orderData.delivery_boy?.mobile || null,
      },
      status: orderData.status?.delivery_status?.toUpperCase() || "UNASSIGNED",
      eta: null,
      distance: `${orderData.customer_distance || 0} km`,
    };
  }

  transformRestaurant(orderData) {
    return {
      location: {
        lat: orderData.restaurant_details?.restaurant_lat || 0,
        lng: orderData.restaurant_details?.restaurant_lng || 0,
      },
    };
  }

  transformOrderMeta(orderData) {
    return {
      prepTimePredicted: orderData.prep_time_predicted || 0,
      orderedTime: orderData.status?.ordered_time,
      placedTime: orderData.status?.placed_time,
      placingState: orderData.status?.placingState,
      earlyMfrTime: orderData.status?.early_mfr_time || 0,
      foodHandoverTimeSec: orderData.foodHandoverTimeSec || 0,
      discountDescriptions: orderData.discount_descriptions || [],
      messages: [],
      nextStates: [],
      delayInfo: {
        isDelayed: orderData.status?.hand_over_delayed || false,
      },
    };
  }

  getUnifiedStatus(status) {
    const { order_status, delivery_status, placed_status } = status;

    const statusMap = {
      ordered: "NEW",
      accepted: "ACCEPTED",
      preparing: "PREPARING",
      ready: "READY",
      dispatched: "DISPATCHED",
      delivered: "DELIVERED",
      cancelled: "CANCELLED",
    };

    if (order_status === "ordered" && placed_status == "unplaced") {
      return UNIFIED_ORDER_STATUS.NEW;
    } else if (order_status === "ordered" && placed_status === "placed") {
      return UNIFIED_ORDER_STATUS.PREPARING;
    } else if (
      order_status === "ordered" &&
      (delivery_status === "confirmed" || delivery_status === "arrived")
    ) {
      return UNIFIED_ORDER_STATUS.READY;
    } else if (
      order_status === "ordered" &&
      delivery_status === "out_of_stock"
    ) {
      return UNIFIED_ORDER_STATUS.CANCELLED;
    } else if (delivery_status === "pickedup") {
      return UNIFIED_ORDER_STATUS.PICKEDUP;
    } else if (order_status === "delivered") {
      return UNIFIED_ORDER_STATUS.DELIVERED;
    } else {
      return UNIFIED_ORDER_STATUS.UNKNOWN;
    }
  }

  getUnifiedDeliveryStatus(status) {
    const statusMap = {
      unassigned: UNIFIED_DELIVERY_STATUS.UNASSIGNED,
      assigned: UNIFIED_DELIVERY_STATUS.ASSIGNED,
      arrived: UNIFIED_DELIVERY_STATUS.ARRIVED,
      pickedup: UNIFIED_DELIVERY_STATUS.PICKED,
      delivered: UNIFIED_DELIVERY_STATUS.DELIVERED,
      cancelled: UNIFIED_DELIVERY_STATUS.CANCELLED,
    };
    return statusMap[status] || status;
  }

  calculateSwiggyTotalTax(data) {
    if (!data?.GST_details) return 0;
    return (
      (data.GST_details.cartCGST || 0) +
      (data.GST_details.cartSGST || 0) +
      (data.GST_details.cartIGST || 0)
    );
  }

  getPrepTime(order) {
    const prepTime = order.status?.food_prep_time || order.promise_prep_time || order.prep_time_predicted;
    const { max_increase_threshold, max_decrease_threshold } =
      order.prep_time_details;

    const maxTime = prepTime + max_increase_threshold;
    const minTime = prepTime - max_decrease_threshold;

    return {
      max: maxTime,
      min: minTime,
      time: prepTime,
      step: 1,
      isDelayAllowed: true,
    };
  }
}
